<template>
  <div class="min-h-screen bg-background dark:bg-[hsl(214_60%_4%)] flex flex-col">
    <!-- Ambient aurora gradient (dark mode only) -->
    <div
      aria-hidden="true"
      class="pointer-events-none fixed inset-0 z-0 hidden dark:block overflow-hidden"
    >
      <div
        class="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.07]"
        style="background: radial-gradient(circle, #00AAD2, transparent 70%)"
      />
      <div
        class="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.05]"
        style="background: radial-gradient(circle, #002C5F, transparent 70%)"
      />
    </div>

    <!-- Persistent top navigation -->
    <AppHeader
      :is-dark="isDark"
      :user-name="auth.user?.name ?? undefined"
      :show-nav="showNav"
      :show-sign-out="!!auth.isAuthenticated && route.path !== '/login'"
      @toggle-dark="toggleDark"
      @sign-out="void auth.logout()"
      @open-profile="profilePanelOpen = true"
    />

    <!-- Driver profile settings panel -->
    <DriverProfilePanel
      v-if="profilePanelOpen"
      @close="profilePanelOpen = false"
      @sign-out="void auth.logout()"
    />

    <main
      id="main-content"
      class="relative z-10 flex-1"
    >
      <RouteErrorBoundary>
        <RouterView />
      </RouteErrorBoundary>
    </main>

    <footer class="relative z-10 py-3 text-center border-t border-border/30">
      <a
        href="https://rsi.digify.no"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="RSI Platform status (opens in new tab)"
        class="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        Powered by RSI Platform
      </a>
    </footer>

    <div
      aria-live="assertive"
      aria-atomic="true"
      class="sr-only"
      id="route-announcer"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDriverProfile } from '@/composables/useDriverProfile'
import AppHeader from '@/components/AppHeader.vue'
import DriverProfilePanel from '@/components/DriverProfilePanel.vue'
import RouteErrorBoundary from '@/components/RouteErrorBoundary.vue'

const route = useRoute()
const auth = useAuthStore()
const { profile } = useDriverProfile()
const profilePanelOpen = ref(false)

function applyThemeAccent(accent: 'neon' | 'classic' | 'minimal') {
  document.documentElement.dataset.themeAccent = accent
}

watch(() => route.path, () => {
  const el = document.getElementById('route-announcer')
  if (!el) return
  el.textContent = ''
  requestAnimationFrame(() => {
    el.textContent = document.title
  })
})

// Don't show main nav on login page
const showNav = computed(() => route.path !== '/login')

const STORAGE_KEY = 'color-scheme'
// public/theme-init.js sets the correct dark/light class on <html> synchronously
// before Vue mounts, eliminating the flash. Mirror its result here.
const isDark = ref(document.documentElement.classList.contains('dark'))

onMounted(() => {
  applyThemeAccent(profile.value.themeAccent)
})

watch(() => profile.value.themeAccent, applyThemeAccent)

function toggleDark() {
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('dark', isDark.value)
  localStorage.setItem(STORAGE_KEY, isDark.value ? 'dark' : 'light')
}
</script>
